import Mustache from "mustache";
import {
    APP_SITE_ID,
    COMPANY_LOGO_EMAIL_URL,
    resolveAppIconEmailUrl,
    resolveCanonicalPublicAssetUrl,
    resolveCanonicalPublicContactUrl,
    resolvePublicLinkUrl,
} from "../../config/appSiteLinks";
import {
    JIGSAW_MUSE_GOOGLE_PLAY_URL,
    SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../config/nativeAppsVisibility";
import {
    cardPaletteForWord,
    DEMO_GUESS_POST,
    fetchLatestPostedWordWheelSocial,
    gradientCssForWord,
    maskWordSlots,
} from "../../pages/landing/wordWheelSocialApi";
import Logger from "../../services/Logger";
import {
    DEFAULT_PROMOTION_SUBJECT,
    DEFAULT_SLIDING_PROMOTION_SUBJECT,
    DEFAULT_WEB_PROMOTION_SUBJECT,
    DEFAULT_WORD_WHEEL_PROMOTION_SUBJECT,
} from "./PromotionEmailHtml";
import ctaOnePartial from "./promotion/_cta_one.mustache";
import signoffPartial from "./promotion/_signoff.mustache";
import jigsawMuseTemplate from "./promotion/jigsaw_muse.mustache";
import slidingTemplate from "./promotion/sliding_puzzle_quest.mustache";
import webTemplate from "./promotion/web.mustache";
import wordWheelTemplate from "./promotion/word_wheel_quest.mustache";

const PARTIALS = {
    _cta_one: ctaOnePartial,
    _signoff: signoffPartial,
};

const JIGSAW_PLAY_BASE = "https://www.puzzleinteract.com/puzzle/play-jigsaw";

const JIGSAW_PUZZLES = [
    {
        title: "Morning Light Meditation Corner",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/688ee030-e077-4800-b0a3-3cfbac05c563/d02ed6a3-38b2-41b0-89f7-3681c5919ae6.png",
        playUrl: JIGSAW_MUSE_GOOGLE_PLAY_URL,
    },
    {
        title: "Caramel Apple Tarte Tatin Slice",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/95a4b7af-c513-403f-a7e0-7bb2d89cb23a/0e8bdfd8-f60f-4be7-8b61-1e267727f0ba.png",
        playUrl: JIGSAW_MUSE_GOOGLE_PLAY_URL,
    },
    {
        title: "Aurora Over Frozen Coastline",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/ffda647e-1c5a-4df5-8d95-3cfe33eaf584/db6878fc-8984-4759-8215-ce4ccec23f2e.png",
        playUrl: JIGSAW_MUSE_GOOGLE_PLAY_URL,
    },
    {
        title: "Lace and Pearls Bridal Still Life",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/025e24a0-80bd-46d5-8ccc-98cc4d5bcd8e/f3b08eaf-1cd1-4e12-a8ec-b8d0db0f0e30.png",
        playUrl: `${JIGSAW_PLAY_BASE}/025e24a0-80bd-46d5-8ccc-98cc4d5bcd8e`,
    },
    {
        title: "Golden Hour at Rajasthani Haveli",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/bae2fc88-a2c3-4367-954b-b60b8ac71b74/d610b3f6-0270-4118-88c2-9fea57df2f8f.png",
        playUrl: `${JIGSAW_PLAY_BASE}/bae2fc88-a2c3-4367-954b-b60b8ac71b74`,
    },
    {
        title: "Peggy's Cove at Golden Hour",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/standalone/b6bcf23e-8e13-4a65-9bc3-5a8030d9043d/b6dd645a-76cb-491b-8211-52edbc3462e3.png",
        playUrl: `${JIGSAW_PLAY_BASE}/b6bcf23e-8e13-4a65-9bc3-5a8030d9043d`,
    },
];

function cta(href, label, primary) {
    return { href, label, primary };
}

function chunkIntoRows(items, perRow) {
    const rows = [];
    const list = Array.isArray(items) ? items : [];
    for (let i = 0; i < list.length; i += perRow) {
        const cells = list.slice(i, i + perRow).map((item) => ({ ...item, empty: false }));
        while (cells.length < perRow) {
            cells.push({ empty: true, title: "", imageUrl: "", playUrl: "" });
        }
        rows.push({ cells });
    }
    return rows;
}

function normalizePromoWord(raw) {
    return String(raw || "")
        .toUpperCase()
        .replace(/[^A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/g, "");
}

function wordWheelGuessView(post) {
    const word = normalizePromoWord(post?.word);
    const slots = word ? maskWordSlots(word, post?.maskedWord) : [];
    const palette = cardPaletteForWord(word);
    return {
        meaning: String(post?.meaning || "").trim(),
        wordSlots: slots.map((letter) => ({ letter: letter || "" })),
        cardBg: palette.from,
        cardGradient: gradientCssForWord(word),
    };
}

async function loadWordWheelGuessPost() {
    try {
        const post = await fetchLatestPostedWordWheelSocial();
        if (post?.word) return post;
    } catch (err) {
        Logger.error("Today’s word for Word Wheel promotion template:", err);
    }
    return DEMO_GUESS_POST;
}

function baseVars(app) {
    return {
        companyLogoUrl: COMPANY_LOGO_EMAIL_URL,
        siteUrl: resolveCanonicalPublicAssetUrl("/"),
        appName: "",
        firstName: "{{firstName}}",
        unsubscribeUrl: "{{unsubscribeUrl}}",
        feedbackUrl: resolveCanonicalPublicContactUrl({ type: "feedback", app }),
        privacyUrl: resolvePublicLinkUrl(app, "privacy"),
        appLogoUrl: resolveAppIconEmailUrl(app),
    };
}

async function varsForApp(app) {
    if (app === APP_SITE_ID.WEB) {
        const siteUrl = resolveCanonicalPublicAssetUrl("/");
        const wordWheelPlayUrl = resolveCanonicalPublicAssetUrl("/puzzles/word-wheel");
        const wordWheelJourneyUrl = resolveCanonicalPublicAssetUrl("/puzzles/word-wheel/journey");
        const wordWheelDailyUrl = resolveCanonicalPublicAssetUrl("/puzzles/word-wheel/daily");
        return {
            ...baseVars(app),
            appName: "Puzzle Interact",
            appLogoUrl: COMPANY_LOGO_EMAIL_URL,
            playUrl: siteUrl,
            wordWheelPlayUrl,
            wordWheelJourneyUrl,
            wordWheelDailyUrl,
            wordWheelIconUrl: resolveAppIconEmailUrl(APP_SITE_ID.WORD_WHEEL_QUEST),
            journeyCtas: [cta(wordWheelJourneyUrl, "🗺️ Continue Journey", false)],
            playCtas: [cta(wordWheelDailyUrl, "👉 Play today’s Daily", true)],
        };
    }
    if (app === APP_SITE_ID.JIGSAW_MUSE) {
        return {
            ...baseVars(app),
            appName: "Jigsaw Muse",
            playUrl: JIGSAW_MUSE_GOOGLE_PLAY_URL,
            puzzleRows: chunkIntoRows(JIGSAW_PUZZLES, 3),
            playCtas: [cta(JIGSAW_MUSE_GOOGLE_PLAY_URL, "👉 Play New Puzzles Now", true)],
            reviewCtas: [
                cta(
                    JIGSAW_MUSE_GOOGLE_PLAY_URL,
                    "Loving the puzzles so far? Leave us a quick rating on Google Play!",
                    false
                ),
            ],
        };
    }
    if (app === APP_SITE_ID.WORD_WHEEL_QUEST) {
        const guess = wordWheelGuessView(await loadWordWheelGuessPost());
        return {
            ...baseVars(app),
            appName: "Word Wheel Quest",
            appVersion: "2.0.1",
            playUrl: WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
            ...guess,
            playCtas: [cta(WORD_WHEEL_QUEST_GOOGLE_PLAY_URL, "👉 Update on Google Play", true)],
            reviewCtas: [cta(WORD_WHEEL_QUEST_GOOGLE_PLAY_URL, "⭐ Leave a Review on Google Play", false)],
        };
    }
    if (app === APP_SITE_ID.SLIDING_PUZZLE_QUEST) {
        return {
            ...baseVars(app),
            appName: "Sliding Puzzle Quest",
            playUrl: SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL,
            playCtas: [cta(SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL, "👉 Play New Puzzles Now", true)],
            reviewCtas: [cta(SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL, "⭐ Leave a Review on Google Play", false)],
        };
    }
    return null;
}

function templateForApp(app) {
    if (app === APP_SITE_ID.WEB) return webTemplate;
    if (app === APP_SITE_ID.JIGSAW_MUSE) return jigsawMuseTemplate;
    if (app === APP_SITE_ID.WORD_WHEEL_QUEST) return wordWheelTemplate;
    if (app === APP_SITE_ID.SLIDING_PUZZLE_QUEST) return slidingTemplate;
    return "";
}

function subjectForApp(app) {
    if (app === APP_SITE_ID.WEB) return DEFAULT_WEB_PROMOTION_SUBJECT;
    if (app === APP_SITE_ID.WORD_WHEEL_QUEST) return DEFAULT_WORD_WHEEL_PROMOTION_SUBJECT;
    if (app === APP_SITE_ID.SLIDING_PUZZLE_QUEST) return DEFAULT_SLIDING_PROMOTION_SUBJECT;
    if (app === APP_SITE_ID.JIGSAW_MUSE) return DEFAULT_PROMOTION_SUBJECT;
    return "";
}

/**
 * Render a frontend Mustache file. Recipient tags stay in the HTML for Java to fill at send.
 */
export async function renderPromotionTemplate(app) {
    const template = templateForApp(app);
    if (!template) {
        return { subject: "", htmlContent: "" };
    }
    const view = await varsForApp(app);
    return {
        subject: subjectForApp(app),
        htmlContent: Mustache.render(template, view, PARTIALS),
    };
}
